import React, { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ReportLayout from '../../components/reports/ReportLayout';
import ReportNote from '../../components/reports/ReportNote';
import { useClient } from '../../context/ClientContext';
import {
  apAgingService,
  AP_AGING_BUCKETS,
  AP_AGING_BUCKET_LABELS
} from '../../services/apAgingService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  downloadAPAgingCsv,
  downloadAPAgingPdf
} from '../../utils/apAgingExport';

const {
  FiAlertTriangle,
  FiChevronDown,
  FiChevronRight,
  FiRefreshCw
} = FiIcons;

const getInitialDate = () => new Date().toISOString().split('T')[0];

const getOverdueTotal = (report) => AP_AGING_BUCKETS
  .filter((bucket) => bucket !== 'current')
  .reduce(
    (total, bucket) => total + Number(report?.grandTotals?.buckets?.[bucket] || 0),
    0
  );

function AgingOverview({ report }) {
  const overdueTotal = getOverdueTotal(report);

  return (
    <section className="mb-7 border-b border-slate-200 pb-5">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Aging overview
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Vendor balances as of the selected date
          </p>
        </div>
        <p className="text-xs text-slate-500">
          Overdue:{' '}
          <span className="font-semibold text-slate-800">
            {formatCurrency(overdueTotal)}
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-5">
        {AP_AGING_BUCKETS.map((bucket) => (
          <div
            key={bucket}
            className="flex items-center justify-between gap-4 border-b border-slate-100 py-2 text-sm lg:border-b-0"
          >
            <span className="text-slate-500">
              {AP_AGING_BUCKET_LABELS[bucket]}
            </span>
            <span className="font-medium tabular-nums text-slate-900">
              {formatCurrency(report.grandTotals.buckets[bucket])}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-slate-300 pt-3 text-sm font-semibold text-slate-900">
        <span>Total Payables</span>
        <span className="tabular-nums">
          {formatCurrency(report.grandTotals.total)}
        </span>
      </div>
    </section>
  );
}

function BillDetails({ bills }) {
  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
      <div className="mb-2 grid grid-cols-2 gap-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:grid-cols-5">
        <span>Bill Number</span>
        <span>Bill Date</span>
        <span>Due Date</span>
        <span className="text-right">Outstanding</span>
        <span className="text-right">Aging Bucket</span>
      </div>

      {bills.map((bill) => (
        <div
          key={bill.id}
          className="grid grid-cols-2 gap-3 border-b border-slate-100 py-2 text-xs last:border-0 sm:grid-cols-5"
        >
          <span className="font-medium text-slate-700">{bill.number}</span>
          <span className="text-slate-500">{formatDate(bill.issueDate)}</span>
          <span className="text-slate-500">{formatDate(bill.dueDate)}</span>
          <span className="text-right font-medium tabular-nums text-slate-700">
            {formatCurrency(bill.amount)}
          </span>
          <span className="text-right text-slate-500">
            {AP_AGING_BUCKET_LABELS[bill.bucket]}
          </span>
        </div>
      ))}
    </div>
  );
}

function VendorAgingTable({ vendors, grandTotals }) {
  const [expandedVendor, setExpandedVendor] = useState(null);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[800px] w-full border-collapse">
        <thead>
          <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            <th className="px-3 py-3 text-left">Vendor</th>
            {AP_AGING_BUCKETS.map((bucket) => (
              <th key={bucket} className="px-3 py-3 text-right">
                {AP_AGING_BUCKET_LABELS[bucket]}
              </th>
            ))}
            <th className="px-3 py-3 text-right">Total</th>
          </tr>
        </thead>

        <tbody>
          {vendors.map((vendor) => {
            const expanded = expandedVendor === vendor.id;

            return (
              <React.Fragment key={vendor.id}>
                <tr className="border-b border-slate-100 transition-colors hover:bg-slate-50/70">
                  <td className="px-3 py-3 text-left">
                    <button
                      type="button"
                      onClick={() => setExpandedVendor(expanded ? null : vendor.id)}
                      className="flex items-center gap-2 text-left font-medium text-slate-800"
                      aria-expanded={expanded}
                    >
                      <SafeIcon
                        icon={expanded ? FiChevronDown : FiChevronRight}
                        className="text-xs text-slate-400"
                      />
                      <span>{vendor.name}</span>
                      <span className="text-[11px] font-normal text-slate-400">
                        {vendor.bills.length} bill{vendor.bills.length === 1 ? '' : 's'}
                      </span>
                    </button>
                  </td>

                  {AP_AGING_BUCKETS.map((bucket) => (
                    <td
                      key={bucket}
                      className="px-3 py-3 text-right tabular-nums text-slate-700"
                    >
                      {formatCurrency(vendor.buckets[bucket])}
                    </td>
                  ))}

                  <td className="bg-slate-50/70 px-3 py-3 text-right font-semibold tabular-nums text-slate-900">
                    {formatCurrency(vendor.total)}
                  </td>
                </tr>

                {expanded && (
                  <tr className="border-b border-slate-100">
                    <td colSpan={7} className="p-0">
                      <BillDetails bills={vendor.bills} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>

        <tfoot>
          <tr className="border-t-2 border-slate-800 text-sm font-semibold text-slate-900">
            <td className="px-3 py-4 text-left">Total Payables</td>
            {AP_AGING_BUCKETS.map((bucket) => (
              <td key={bucket} className="px-3 py-4 text-right tabular-nums">
                {formatCurrency(grandTotals.buckets[bucket])}
              </td>
            ))}
            <td className="px-3 py-4 text-right tabular-nums">
              {formatCurrency(grandTotals.total)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Diagnostics({ diagnostics }) {
  if (!diagnostics) return null;

  return (
    <details className="mt-6 border-t border-slate-100 pt-4 text-xs text-slate-400">
      <summary className="cursor-pointer select-none hover:text-slate-600">
        Data verification
      </summary>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <span>Vendors: {diagnostics.vendorsFound}</span>
        <span>Bills: {diagnostics.billsFound}</span>
        <span>Included: {diagnostics.includedBills}</span>
        <span>Excluded: {diagnostics.excludedBills}</span>
        <span>
          Outstanding: {formatCurrency(diagnostics.totalOutstandingBalance)}
        </span>
      </div>
    </details>
  );
}

export default function APAgingReport() {
  const { activeClient } = useClient();
  const [asOfDate, setAsOfDate] = useState(getInitialDate);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const fetchReport = async () => {
    if (!activeClient?.id) {
      setReport(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const businessName = activeClient.legal_name
        || activeClient.name
        || 'Your Business';

      const data = await apAgingService.generate(
        asOfDate,
        activeClient.id,
        businessName
      );

      setReport(data);
    } catch (fetchError) {
      console.error('[AP Aging] Report generation failed', fetchError);
      setError(
        fetchError.message
          || 'Failed to generate Accounts Payable Aging report'
      );
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [activeClient?.id, asOfDate]);

  const handleExport = (format) => {
    if (!report) return;

    try {
      if (format === 'csv') {
        downloadAPAgingCsv({ report, asOfDate });
      }

      if (format === 'pdf') {
        downloadAPAgingPdf({ report, asOfDate });
      }
    } catch (exportError) {
      console.error('[AP Aging] Export failed', exportError);
      setError(exportError.message || 'Unable to export the AP Aging report');
    } finally {
      setExportOpen(false);
    }
  };

  return (
    <ReportLayout
      title="Accounts Payable Aging"
      description="Outstanding vendor balances organized by aging period."
      viewMode="details"
      setViewMode={() => {}}
      endDate={asOfDate}
      onEndDateChange={setAsOfDate}
      onRefresh={fetchReport}
      loading={loading}
      onExport={handleExport}
      exportOpen={exportOpen}
      setExportOpen={setExportOpen}
      asOfOnly
      showTabs={false}
    >
      {!activeClient && !loading && (
        <p className="py-10 text-center text-sm text-slate-500">
          Select a business to view accounts payable.
        </p>
      )}

      {error && (
        <div className="mb-5 flex items-center justify-between gap-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiAlertTriangle} />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={fetchReport}
            className="shrink-0 font-medium underline"
          >
            Try again
          </button>
        </div>
      )}

      {report && <AgingOverview report={report} />}

      {report?.vendors?.length ? (
        <VendorAgingTable
          vendors={report.vendors}
          grandTotals={report.grandTotals}
        />
      ) : (
        report && (
          <div className="py-10 text-center">
            <p className="font-medium text-slate-700">
              {report.emptyReason || 'No outstanding payables found for the selected date.'}
            </p>
            <p className="mt-1 text-sm text-slate-400">
              Paid, draft, void, and zero-balance bills are excluded.
            </p>
          </div>
        )
      )}

      {report && <Diagnostics diagnostics={report.diagnostics} />}

      {report && (
        <div className="mt-8 opacity-70">
          <ReportNote />
        </div>
      )}
    </ReportLayout>
  );
}